import React, { useEffect, useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Card } from '../../components/UI'
import { Eye, Database, Terminal, Scale, Download, ChevronDown, RefreshCw, FileText } from 'lucide-react'

// ============================================
// Constantes de categorías de datos
// ============================================

const DATA_CATEGORIES = [
    {
        category: 'Sesiones',
        eventType: 'session_start',
        icon: '🖥️',
        description: 'Inicio de sesión del usuario',
        fields: ['navegador (sin UA completo)', 'idioma', 'SID único por sesión'],
        legal: 'Base legal: Interés legítimo (Art. 6.1.f) — Control de concurrencia'
    },
    {
        category: 'Pageviews',
        eventType: 'pageview',
        icon: '📄',
        description: 'Navegación entre páginas',
        fields: ['URL (sin querystrings)', 'referrer (dominio solo)', 'SID'],
        legal: 'Base legal: Interés legítimo — Analítica de uso'
    },
    {
        category: 'Errores',
        eventType: 'error',
        icon: '⚠️',
        description: 'Errores JavaScript capturados',
        fields: ['mensaje de error', 'stack trace (sanitizado)', 'URL (sanitizada)'],
        legal: 'Base legal: Interés legítimo — Soporte técnico'
    },
    {
        category: 'Performance',
        eventType: 'performance',
        icon: '⚡',
        description: 'Métricas de rendimiento (Core Web Vitals)',
        fields: ['LCP', 'FID', 'CLS', 'TTFB'],
        legal: 'Base legal: Interés legítimo — Calidad del servicio'
    },
]

const RGPD_RIGHTS = [
    {
        article: 'Art. 15',
        title: 'Derecho de acceso',
        description: 'El cliente tiene derecho a saber qué datos se están recopilando sobre sus usuarios.',
        status: 'active',
        action: 'Los datos se muestran en la pestaña "Datos Recopilados" de este panel.'
    },
    {
        article: 'Art. 16',
        title: 'Derecho de rectificación',
        description: 'El cliente puede solicitar la corrección de datos inexactos.',
        status: 'active',
        action: 'Contactar vía soporte para rectificación de datos erróneos.'
    },
    {
        article: 'Art. 17',
        title: 'Derecho de supresión',
        description: 'El cliente puede solicitar la eliminación de todos sus datos de telemetría.',
        status: 'planned',
        action: 'Solicitar al administrador la purga completa de eventos de la licencia.'
    },
    {
        article: 'Art. 20',
        title: 'Derecho de portabilidad',
        description: 'El cliente puede exportar sus datos en formato JSON estándar.',
        status: 'active',
        action: 'Usar el botón "Exportar datos" en la pestaña de datos.'
    },
    {
        article: 'Art. 21',
        title: 'Derecho de oposición',
        description: 'El cliente puede oponerse al tratamiento basado en interés legítimo.',
        status: 'active',
        action: 'Desactivar la telemetría a nivel de licencia configurando VITE_SKIP_DRM=true.'
    },
]

// ============================================
// Page Component
// ============================================

export default function TransparencyPage() {
    const [licenses, setLicenses] = useState([])
    const [selectedLicense, setSelectedLicense] = useState(null)
    const [activeTab, setActiveTab] = useState('data')
    const [loading, setLoading] = useState(true)
    const [events, setEvents] = useState([])
    const [commands, setCommands] = useState([])
    const [eventsLoading, setEventsLoading] = useState(false)

    useEffect(() => {
        fetchLicenses()
    }, [])

    useEffect(() => {
        if (selectedLicense) {
            fetchLicenseData(selectedLicense)
        }
    }, [selectedLicense])

    const fetchLicenses = async () => {
        try {
            const { data, error } = await supabase
                .from('licenses')
                .select('id, client_name, client_id, projects(name)')
                .order('client_name')
            if (error) throw error
            setLicenses(data || [])
            if (data?.length > 0) setSelectedLicense(data[0].id)
        } catch (e) {
            toast.error('Error cargando licencias')
        } finally {
            setLoading(false)
        }
    }

    const fetchLicenseData = async (licenseId) => {
        setEventsLoading(true)
        try {
            // Telemetry events (últimos 50)
            const { data: evtData } = await supabase.rpc('ai_diagnose_license', { p_license_id: licenseId })

            // También traer últimos eventos de telemetría directamente via RPC
            const { data: recentEvents } = await supabase
                .from('telemetry_events')
                .select('event_type, created_at, payload')
                .eq('license_id', licenseId)
                .order('created_at', { ascending: false })
                .limit(50)

            setEvents(recentEvents || [])

            // Remote commands
            const { data: cmds } = await supabase
                .from('remote_commands')
                .select('*')
                .eq('license_id', licenseId)
                .order('created_at', { ascending: false })
                .limit(30)

            setCommands(cmds || [])
        } catch (e) {
            console.error(e)
        } finally {
            setEventsLoading(false)
        }
    }

    const exportData = () => {
        const lic = licenses.find(l => l.id === selectedLicense)
        const exportObj = {
            _meta: {
                exported_at: new Date().toISOString(),
                license_id: selectedLicense,
                client_name: lic?.client_name,
                format: 'RGPD Art. 20 — Portabilidad de datos',
            },
            telemetry_events: events,
            remote_commands: commands,
        }
        const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `torre_data_${lic?.client_name?.replace(/\s+/g, '_') || 'export'}_${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
        toast.success('Datos exportados correctamente')
    }

    const tabs = [
        { id: 'data', label: 'Datos Recopilados', icon: Database },
        { id: 'commands', label: 'Comandos', icon: Terminal },
        { id: 'rights', label: 'Derechos RGPD', icon: Scale },
    ]

    const selectedLic = licenses.find(l => l.id === selectedLicense)

    return (
        <div className="p-6 max-w-6xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Eye className="text-neon-blue" /> Transparencia
                </h1>
                <p className="text-gray-500 text-sm">Dashboard de datos recopilados — RGPD Art. 15 (Derecho de acceso)</p>
            </div>

            <FeatureNotEnabled moduleName="Transparencia" />

            {/* Selector de Licencia */}
            <Card className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 w-full">
                    <label className="text-xs text-gray-400 block mb-1.5">Seleccionar licencia</label>
                    <div className="relative">
                        <select
                            value={selectedLicense || ''}
                            onChange={e => setSelectedLicense(e.target.value)}
                            className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white appearance-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue outline-none transition-all"
                        >
                            {licenses.map(l => (
                                <option key={l.id} value={l.id}>
                                    {l.client_name} — {l.projects?.name || 'Sin proyecto'}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>
                <div className="flex gap-2 sm:self-end">
                    <button
                        onClick={() => selectedLicense && fetchLicenseData(selectedLicense)}
                        className="px-4 py-3 bg-white/5 hover:bg-white/10 text-gray-400 rounded-lg border border-white/10 transition-all flex items-center gap-2"
                    >
                        <RefreshCw size={14} /> Refrescar
                    </button>
                    <button
                        onClick={exportData}
                        className="px-4 py-3 bg-neon-blue/10 hover:bg-neon-blue/20 text-neon-blue rounded-lg border border-neon-blue/20 transition-all flex items-center gap-2"
                    >
                        <Download size={14} /> Exportar JSON
                    </button>
                </div>
            </Card>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-white/[0.03] p-1 rounded-xl border border-white/5">
                {tabs.map(tab => {
                    const Icon = tab.icon
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                                ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <Icon size={14} />
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    )
                })}
            </div>

            {/* Content */}
            {eventsLoading ? (
                <div className="text-center py-20 text-gray-500">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-3" />
                    Cargando datos...
                </div>
            ) : activeTab === 'data' ? (
                <DataTab events={events} licName={selectedLic?.client_name} />
            ) : activeTab === 'commands' ? (
                <CommandsTab commands={commands} />
            ) : (
                <RightsTab />
            )}
        </div>
    )
}

// ============================================
// Tabs
// ============================================

function DataTab({ events, licName }) {
    const counts = {}
    events.forEach(e => { counts[e.event_type] = (counts[e.event_type] || 0) + 1 })

    return (
        <div className="space-y-6">
            {/* Categorías */}
            {DATA_CATEGORIES.map(cat => {
                const catEvents = events.filter(e => e.event_type === cat.eventType)
                return (
                    <Card key={cat.category} className="border border-white/5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{cat.icon}</span>
                                <div>
                                    <h3 className="text-white font-bold text-sm">{cat.category}</h3>
                                    <p className="text-gray-500 text-xs">{cat.description}</p>
                                </div>
                            </div>
                            <span className="px-3 py-1 rounded-full bg-white/5 text-gray-400 text-xs font-mono">
                                {counts[cat.eventType] || 0} eventos
                            </span>
                        </div>

                        {/* Campos capturados */}
                        <div className="mb-3">
                            <div className="text-[10px] uppercase text-gray-500 mb-1.5">Campos capturados:</div>
                            <div className="flex flex-wrap gap-1.5">
                                {cat.fields.map(f => (
                                    <span key={f} className="px-2 py-0.5 bg-white/5 text-gray-400 rounded text-xs border border-white/5">{f}</span>
                                ))}
                            </div>
                        </div>

                        {/* Base legal */}
                        <div className="text-[10px] text-gray-600 italic">{cat.legal}</div>

                        {/* Últimos 3 eventos */}
                        {catEvents.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-white/5">
                                <div className="text-[10px] uppercase text-gray-500 mb-2">Últimos eventos:</div>
                                <div className="space-y-1">
                                    {catEvents.slice(0, 3).map((evt, i) => (
                                        <div key={i} className="text-xs font-mono text-gray-500 bg-black/20 px-3 py-1.5 rounded flex justify-between">
                                            <span className="text-gray-400 truncate max-w-[70%]">{JSON.stringify(evt.payload).substring(0, 80)}...</span>
                                            <span className="text-gray-600 shrink-0 ml-2">{new Date(evt.created_at).toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Card>
                )
            })}

            {events.length === 0 && (
                <div className="text-center py-16 text-gray-600">
                    <Database size={32} className="mx-auto mb-3 opacity-50" />
                    <p>No hay eventos de telemetría para esta licencia</p>
                </div>
            )}
        </div>
    )
}

function CommandsTab({ commands }) {
    const statusColors = {
        pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
        acknowledged: 'text-green-400 bg-green-500/10 border-green-500/20',
        executed: 'text-green-400 bg-green-500/10 border-green-500/20',
        expired: 'text-gray-400 bg-gray-500/10 border-gray-500/20',
        failed: 'text-red-400 bg-red-500/10 border-red-500/20',
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-400">{commands.length} comandos en historial</span>
            </div>

            {commands.map(cmd => (
                <div key={cmd.id} className="p-4 bg-white/[0.02] rounded-lg border border-white/5 flex flex-wrap items-start gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Terminal size={14} className="text-neon-blue" />
                            <span className="text-white font-bold text-sm font-mono">{cmd.command}</span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono border ${statusColors[cmd.status] || statusColors.pending}`}>
                                {cmd.status}
                            </span>
                        </div>
                        {cmd.payload && (
                            <pre className="text-xs text-gray-500 font-mono mt-1 bg-black/20 p-2 rounded overflow-x-auto">
                                {JSON.stringify(cmd.payload, null, 2)}
                            </pre>
                        )}
                    </div>
                    <div className="text-right text-xs text-gray-500 shrink-0">
                        <div>{new Date(cmd.created_at).toLocaleDateString()}</div>
                        <div>{new Date(cmd.created_at).toLocaleTimeString()}</div>
                        {cmd.acknowledged_at && (
                            <div className="text-green-500 mt-1">ACK: {new Date(cmd.acknowledged_at).toLocaleTimeString()}</div>
                        )}
                    </div>
                </div>
            ))}

            {commands.length === 0 && (
                <div className="text-center py-16 text-gray-600">
                    <Terminal size={32} className="mx-auto mb-3 opacity-50" />
                    <p>No hay comandos remotos para esta licencia</p>
                </div>
            )}
        </div>
    )
}

function RightsTab() {
    const statusStyles = {
        active: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', label: '✅ Implementado' },
        planned: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', label: '🔄 Planificado' },
        pending: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: '❌ Pendiente' },
    }

    return (
        <div className="space-y-4">
            <Card className="border border-neon-blue/20 bg-neon-blue/5">
                <div className="flex items-start gap-3">
                    <Scale size={20} className="text-neon-blue shrink-0 mt-0.5" />
                    <div>
                        <h3 className="text-white font-bold text-sm mb-1">Derechos del Cliente (RGPD)</h3>
                        <p className="text-gray-400 text-xs leading-relaxed">
                            El Reglamento General de Protección de Datos (RGPD) otorga a los interesados una serie de derechos
                            sobre sus datos personales. A continuación se detalla el estado de implementación de cada derecho
                            en el sistema Torre de Control.
                        </p>
                    </div>
                </div>
            </Card>

            {RGPD_RIGHTS.map((right, i) => {
                const s = statusStyles[right.status]
                return (
                    <div key={i} className={`p-5 rounded-lg border ${s.border} bg-white/[0.02]`}>
                        <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono border ${s.border} ${s.bg} ${s.text}`}>
                                {right.article}
                            </span>
                            <h3 className="text-white font-bold text-sm">{right.title}</h3>
                            <span className={`ml-auto px-2 py-0.5 rounded text-[10px] ${s.bg} ${s.text} border ${s.border}`}>
                                {s.label}
                            </span>
                        </div>
                        <p className="text-gray-400 text-sm mb-2">{right.description}</p>
                        <div className="flex items-start gap-2 text-xs text-gray-500 bg-black/20 p-2.5 rounded">
                            <FileText size={12} className="shrink-0 mt-0.5" />
                            <span>{right.action}</span>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
